import {Box, Heading, Show, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {all, always, any, filter, length, pipe, propEq, values} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {buildArray, EMPTY_PLACEHOLDER, Nullish} from 'shared/web';

import {Alert} from '../../components/Alert/Alert';
import {Carousel} from '../../components/Carousel/Carousel';
import {Comment} from '../../components/Comment/CommentExpandable';
import {ListFeaturesItemProps} from '../../components/ListFeatures/components/ListFeaturesItem';
import {ListFeatures} from '../../components/ListFeatures/ListFeatures';
import {Section} from '../../components/Section/Section';
import {SectionStateFlagProps} from '../../components/SectionStateFlag/SectionStateFlag';
import {Separator} from '../../components/Separator/Separator';
import {MechanicsDataBundle} from '../../hooks/useGetDataForMechanics';
import i18n from '../../i18n/i18n';
import {filterListFeatures} from '../../utils/filterListFeatures';
import {getMechanicFeatures} from '../../utils/getMechanicFeatures';
import {getRecordsTranslate} from '../../utils/getRecordsTranslate';
import {getValueWithUnit} from '../../utils/getValueWithUnit';
import {isB2cCarAuditWorkspace} from '../../utils/isB2cCarAuditWorkspace';
import {getBrakeLiningWearSeverity} from './utils/getBrakeLiningWearSeverity';
import {hasBrakeDiscInfo} from './utils/hasBrakeDiscInfo';
import {hasBrakeLiningWearInfo} from './utils/hasBrakeLiningWearInfo';
import {hasBrakePadInfo} from './utils/hasBrakePadInfo';

interface MechanicsProps extends Omit<MechanicsDataBundle, 'sectionConfig'> {
  showEmptyFields: boolean;
}

export function Mechanics({mechanicSlideshow, mechanics, showEmptyFields}: MechanicsProps) {
  if (!mechanics) {
    return null;
  }

  const features = getMechanicFeatures(mechanics, showEmptyFields);

  if (!features) {
    return null;
  }

  const allFeatures = features.allFeatures;

  const {
    brakeSystemFeatures,
    electricalSystemFeatures,
    driveSystemFeatures,
    diagnosticsFeatures,
    chassisFeatures,
    bodyConditionFeatures,
  } = features.features;

  const isNotTested = all((f) => f.variant === 'neutral', allFeatures);
  const isDamaged = any((f) => f.variant === 'damaged', allFeatures);
  const countDamages: (object: object) => number = pipe(
    values,
    filter(propEq('damaged', 'variant')),
    length
  );

  const brakePad = {
    lf: {
      valueWithUnit: getValueWithUnit({
        auditValue: {
          value: mechanics.brake.brakePad.LF.value,
          unit: mechanics.brake.brakePad.LF.unit,
        },
      }),
      severity: mechanics.brake.brakePad.LF.value ? 'ready' : 'neutral',
    },
    rf: {
      valueWithUnit: getValueWithUnit({
        auditValue: {
          value: mechanics.brake.brakePad.RF.value,
          unit: mechanics.brake.brakePad.RF.unit,
        },
      }),
      severity: mechanics.brake.brakePad.RF.value ? 'ready' : 'neutral',
    },
    lr: {
      valueWithUnit: getValueWithUnit({
        auditValue: {
          value: mechanics.brake.brakePad.LR.value,
          unit: mechanics.brake.brakePad.LR.unit,
        },
      }),
      severity: mechanics.brake.brakePad.LR.value ? 'ready' : 'neutral',
    },
    rr: {
      valueWithUnit: getValueWithUnit({
        auditValue: {
          value: mechanics.brake.brakePad.RR.value,
          unit: mechanics.brake.brakePad.RR.unit,
        },
      }),
      severity: mechanics.brake.brakePad.RR.value ? 'ready' : 'neutral',
    },
  } as const;

  const brakeLiningWear = {
    lf: {
      valueWithUnit: getValueWithUnit({
        auditValue: {
          value: mechanics.brake.brakeLiningWear.LF.value,
          unit: mechanics.brake.brakeLiningWear.LF.unit,
        },
      }),
      severity: getBrakeLiningWearSeverity(mechanics.brake.brakeLiningWear.LF.value),
    },
    rf: {
      valueWithUnit: getValueWithUnit({
        auditValue: {
          value: mechanics.brake.brakeLiningWear.RF.value,
          unit: mechanics.brake.brakeLiningWear.RF.unit,
        },
      }),
      severity: getBrakeLiningWearSeverity(mechanics.brake.brakeLiningWear.RF.value),
    },
    lr: {
      valueWithUnit: getValueWithUnit({
        auditValue: {
          value: mechanics.brake.brakeLiningWear.LR.value,
          unit: mechanics.brake.brakeLiningWear.LR.unit,
        },
      }),
      severity: getBrakeLiningWearSeverity(mechanics.brake.brakeLiningWear.LR.value),
    },
    rr: {
      valueWithUnit: getValueWithUnit({
        auditValue: {
          value: mechanics.brake.brakeLiningWear.RR.value,
          unit: mechanics.brake.brakeLiningWear.RR.unit,
        },
      }),
      severity: getBrakeLiningWearSeverity(mechanics.brake.brakeLiningWear.RR.value),
    },
  } as const;

  return (
    <>
      <Section
        id="MECHANICS"
        icon="mechanics"
        heading={i18n.t('mechanicsHeader')}
        content={
          <VStack spacing={[3, 6, 6, 10]}>
            <Show when={showEmptyFields || isNotNilOrEmpty(brakeSystemFeatures)}>
              <Heading size={2}>{i18n.t('brakeSystem')}</Heading>
              <ListFeatures
                columns={[1, 2, 2, 2]}
                spacing={[6, 6, 10, 10]}
                features={brakeSystemFeatures}
              />
            </Show>
            <Show when={hasBrakeDiscInfo(mechanics.brake, showEmptyFields)}>
              <Separator />
              <Heading size={2}>{i18n.t('brakeDisc')}</Heading>
              <ListFeatures
                columns={[1, 2, 2, 2]}
                spacing={[6, 6, 10, 10]}
                features={filterListFeatures(
                  [
                    {
                      title: i18n.t('valueLF'),
                      variant: mechanics.brake.brakeDiscLF.variant,
                      flagTexts: mechanics.brake.brakeDiscLF.flagTexts,
                      description: mechanics.brake.brakeDiscLF.description,
                      tooltipText: mechanics.brake.brakeDiscLF.tooltipText,
                    },
                    {
                      title: i18n.t('valueRF'),
                      variant: mechanics.brake.brakeDiscRF.variant,
                      flagTexts: mechanics.brake.brakeDiscRF.flagTexts,
                      description: mechanics.brake.brakeDiscRF.description,
                      tooltipText: mechanics.brake.brakeDiscRF.tooltipText,
                    },
                    {
                      title: i18n.t('valueLR'),
                      variant: mechanics.brake.brakeDiscLR.variant,
                      flagTexts: mechanics.brake.brakeDiscLR.flagTexts,
                      description: mechanics.brake.brakeDiscLR.description,
                      tooltipText: mechanics.brake.brakeDiscLR.tooltipText,
                    },
                    {
                      title: i18n.t('valueRR'),
                      variant: mechanics.brake.brakeDiscRR.variant,
                      flagTexts: mechanics.brake.brakeDiscRR.flagTexts,
                      description: mechanics.brake.brakeDiscRR.description,
                      tooltipText: mechanics.brake.brakeDiscRR.tooltipText,
                    },
                  ],
                  showEmptyFields
                )}
              />
            </Show>
            <Show when={hasBrakePadInfo(mechanics.brake.brakePad, showEmptyFields)}>
              <Separator />
              <Heading size={2}>{i18n.t('brakePad')}</Heading>
              <ListFeatures
                columns={[1, 2, 2, 2]}
                spacing={[6, 6, 10, 10]}
                features={buildArray<ListFeaturesItemProps>()
                  .when(brakePad.lf.valueWithUnit || showEmptyFields, {
                    title: i18n.t('valueLF'),
                    value: brakePad.lf.valueWithUnit ?? EMPTY_PLACEHOLDER,
                    variant: brakePad.lf.severity,
                  })
                  .when(brakePad.rf.valueWithUnit || showEmptyFields, {
                    title: i18n.t('valueRF'),
                    value: brakePad.rf.valueWithUnit ?? EMPTY_PLACEHOLDER,
                    variant: brakePad.rf.severity,
                  })
                  .when(brakePad.lr.valueWithUnit || showEmptyFields, {
                    title: i18n.t('valueLR'),
                    value: brakePad.lr.valueWithUnit ?? EMPTY_PLACEHOLDER,
                    variant: brakePad.lr.severity,
                  })
                  .when(brakePad.rr.valueWithUnit || showEmptyFields, {
                    title: i18n.t('valueRR'),
                    value: brakePad.rr.valueWithUnit ?? EMPTY_PLACEHOLDER,
                    variant: brakePad.rr.severity,
                  })}
              />
              <Show when={isNotNilOrEmpty(mechanics.brake.brakePad.comment)}>
                <Box maxWidth={250}>
                  <Comment title={i18n.t('note')} comment={mechanics.brake.brakePad.comment} />
                </Box>
              </Show>
            </Show>
            <Show when={hasBrakeLiningWearInfo(mechanics.brake.brakeLiningWear, showEmptyFields)}>
              <Separator />
              <Heading size={2}>{i18n.t('brakeLiningWear')}</Heading>
              <Show when={isB2cCarAuditWorkspace()}>
                <Box width="fit-content">
                  <Alert>{i18n.t('brakeLiningWearValuesAlert')}</Alert>
                </Box>
              </Show>
              <ListFeatures
                columns={[1, 2, 2, 2]}
                spacing={[6, 6, 10, 10]}
                features={buildArray<ListFeaturesItemProps>()
                  .when(brakeLiningWear.lf.valueWithUnit || showEmptyFields, {
                    title: i18n.t('valueLF'),
                    value: brakeLiningWear.lf.valueWithUnit ?? EMPTY_PLACEHOLDER,
                    variant: brakeLiningWear.lf.severity,
                  })
                  .when(brakeLiningWear.rf.valueWithUnit || showEmptyFields, {
                    title: i18n.t('valueRF'),
                    value: brakeLiningWear.rf.valueWithUnit ?? EMPTY_PLACEHOLDER,
                    variant: brakeLiningWear.rf.severity,
                  })
                  .when(brakeLiningWear.lr.valueWithUnit || showEmptyFields, {
                    title: i18n.t('valueLR'),
                    value: brakeLiningWear.lr.valueWithUnit ?? EMPTY_PLACEHOLDER,
                    variant: brakeLiningWear.lr.severity,
                  })
                  .when(brakeLiningWear.rr.valueWithUnit || showEmptyFields, {
                    title: i18n.t('valueRR'),
                    value: brakeLiningWear.rr.valueWithUnit ?? EMPTY_PLACEHOLDER,
                    variant: brakeLiningWear.rr.severity,
                  })}
              />
              <Show when={isNotNilOrEmpty(mechanics.brake.brakeLiningWear.comment)}>
                <Box maxWidth={250}>
                  <Comment
                    title={i18n.t('note')}
                    comment={mechanics.brake.brakeLiningWear.comment}
                  />
                </Box>
              </Show>
            </Show>
            <Show when={showEmptyFields || isNotNilOrEmpty(driveSystemFeatures)}>
              <Separator />
              <Heading size={2}>{i18n.t('driveSystem')}</Heading>
              <ListFeatures
                columns={[1, 2, 2, 2]}
                spacing={[6, 6, 10, 10]}
                features={driveSystemFeatures}
              />
            </Show>
            <Show when={showEmptyFields || isNotNilOrEmpty(chassisFeatures)}>
              <Separator />
              <Heading size={2}>{i18n.t('chassis')}</Heading>
              <ListFeatures
                columns={[1, 2, 2, 2]}
                spacing={[6, 6, 10, 10]}
                features={chassisFeatures}
              />
            </Show>
            <Show when={showEmptyFields || isNotNilOrEmpty(electricalSystemFeatures)}>
              <Separator />
              <Heading size={2}>{i18n.t('electricalSystem')}</Heading>
              <ListFeatures
                columns={[1, 2, 2, 2]}
                spacing={[6, 6, 10, 10]}
                features={electricalSystemFeatures}
              />
            </Show>
            <Show when={showEmptyFields || isNotNilOrEmpty(diagnosticsFeatures)}>
              <Separator />
              <Heading size={2}>{i18n.t('diagnostics')}</Heading>
              <ListFeatures
                columns={[1, 2, 2, 2]}
                spacing={[6, 6, 10, 10]}
                features={diagnosticsFeatures}
              />
            </Show>
            <Show when={showEmptyFields || isNotNilOrEmpty(bodyConditionFeatures)}>
              <Separator />
              <Heading size={2}>{i18n.t('bodyCondition')}</Heading>
              <ListFeatures
                columns={[1, 2, 2, 2]}
                spacing={[6, 6, 10, 10]}
                features={bodyConditionFeatures}
              />
            </Show>
            <Show when={isNotNilOrEmpty(mechanicSlideshow)}>
              <Separator />
              <Carousel isFilterOfDamageDisabled data={mechanicSlideshow} />
            </Show>
          </VStack>
        }
        flag={match<[boolean, boolean], Nullish | SectionStateFlagProps>([isNotTested, isDamaged])
          .with(
            [false, true],
            always({severity: 'damage', text: getRecordsTranslate(countDamages(allFeatures))})
          )
          .with([true, false], always(null))
          .otherwise(always({severity: 'good', text: i18n.t('sectionState.good')}))}
      />
    </>
  );
}
